// ── AccountStore — Zustand store for Kalshi account state ───────
//
// Completely separate from marketStore (Hydra pipeline).
// All fill sources (WS primary, WS failover, poller) feed through
// a single dedup layer via processFill().

import { create } from 'zustand';
import { parsePem, buildHeaders, getBaseUrl } from '../lib/kalshi/auth';
import { KalshiClient, type ParsedKey } from '../clients/KalshiClient';
import { KeyChannel } from '../clients/KeyChannel';
import type {
  KalshiCredentials,
  KalshiPosition,
  KalshiFill,
  KalshiOrder,
  OrderInput,
  KeyHealth,
  AccountStatus,
} from '../types/kalshi';
import { readSoundSettings } from '../lib/audio/soundSettings';
import { playFillTone, playKaChingTone, playWhaleTone, playOrderRejectedTone, playOrderPlacedTone } from '../lib/audio/tradeAudio';
import { getAudioContext, setPendingSound } from '../lib/audio/audioContext';

// ── Constants ───────────────────────────────────────────────────

const STORAGE_KEY = 'g1xl_kalshi_credentials';
const MAX_SEEN_FILLS = 1000;
const MAX_RECENT_FILLS = 100;
const ACTIVITY_TICKER_SIZE = 20;
const POLL_INTERVAL_MS = 500;  // per-channel poll interval
const WS_STAGGER_MS = 2000;   // delay between channel WS connections

// ── Module-level state (not in Zustand — mutable refs) ──────────

let _client: KalshiClient | null = null;
let _channels: KeyChannel[] = [];
const _seenFillIds = new Set<string>();
const _seenFillOrder: string[] = []; // for bounded eviction
const _seenOrderIds = new Set<string>(); // tracks orderIds for ka-ching detection

// ── Store ───────────────────────────────────────────────────────

interface AccountState {
  // Connection
  credentials: KalshiCredentials | null;
  status: AccountStatus;
  error: string | null;
  keyHealth: KeyHealth[];

  // Data
  balance: number | null;
  positions: KalshiPosition[];
  openOrders: KalshiOrder[];
  recentFills: KalshiFill[];
  activityTicker: KalshiFill[];

  // Actions
  connect: (creds: KalshiCredentials) => Promise<void>;
  disconnect: () => void;
  removeCredentials: () => void;
  refreshPositions: () => Promise<void>;
  refreshOrders: () => Promise<void>;
  processFill: (fill: KalshiFill) => void;
  placeOrder: (input: OrderInput) => Promise<KalshiOrder>;
  cancelOrder: (orderId: string) => Promise<void>;
}

export const useAccountStore = create<AccountState>((set, get) => ({
  // ── Initial state ───────────────────────────────────────────
  credentials: null,
  status: 'disconnected',
  error: null,
  keyHealth: [],
  balance: null,
  positions: [],
  openOrders: [],
  recentFills: [],
  activityTicker: [],

  // ── Connect ─────────────────────────────────────────────────
  connect: async (creds: KalshiCredentials) => {
    // Prevent double-connect
    if (get().status === 'connecting') return;
    set({ status: 'connecting', error: null, credentials: creds });

    try {
      // 1. Parse all PEMs → CryptoKeys
      const parsedKeys: ParsedKey[] = [];
      for (const key of creds.keys) {
        try {
          const cryptoKey = await parsePem(key.privateKeyPem);
          parsedKeys.push({ id: key.id, accessKey: key.accessKey, cryptoKey, label: key.label });
        } catch {
          throw new Error(`Invalid PEM for key "${key.label}". Use PKCS#8 format (BEGIN PRIVATE KEY).`);
        }
      }

      if (parsedKeys.length === 0) {
        throw new Error('No valid API keys provided.');
      }

      // 2. Persist credentials early so they survive a failed connection attempt
      localStorage.setItem(STORAGE_KEY, JSON.stringify(creds));

      // 3. Create KeyChannels (one per parsed key)
      _channels.forEach((ch) => ch.stopAll());
      _channels = parsedKeys.map((pk) => new KeyChannel(pk));

      // 4. Create REST client backed by channels
      _client = new KalshiClient(_channels, creds.environment);

      // 5. Test connection with getBalance
      const balance = await _client.getBalance();

      // 6. Start per-channel WebSockets (staggered to avoid thundering herd)
      _channels.forEach((ch, i) => {
        setTimeout(() => {
          ch.startWs(creds.environment, {
            onFill: (fill) => get().processFill(fill),
            onOrderUpdate: () => {
              get().refreshOrders().catch(() => {});
            },
            onStatus: () => {
              // Update all channel health on any WS status change
              set({ keyHealth: _channels.map((c) => c.getHealth()) });
            },
          });
        }, i * WS_STAGGER_MS);
      });

      // 7. Start per-channel fill pollers (staggered within interval)
      _channels.forEach((ch, i) => {
        const stagger = Math.round((i / _channels.length) * POLL_INTERVAL_MS);
        setTimeout(() => {
          ch.startPoller(
            (since) => channelFetchFills(ch, creds.environment, since),
            (fills) => { for (const fill of fills) get().processFill(fill); },
            POLL_INTERVAL_MS,
          );
        }, stagger);
      });

      // 8. Fetch initial data
      const [positions, orders] = await Promise.all([
        _client.getPositions().catch(() => [] as KalshiPosition[]),
        _client.getOpenOrders().catch(() => [] as KalshiOrder[]),
      ]);

      // 9. Build initial key health from channels
      const keyHealth: KeyHealth[] = _channels.map((ch) => ch.getHealth());

      // 10. Set connected (credentials already persisted in step 2)
      set({
        status: 'connected',
        error: null,
        balance,
        positions,
        openOrders: orders,
        keyHealth,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Connection failed';
      console.error('[AccountStore] connect failed:', message);

      // Tear down anything partially created
      _channels.forEach((ch) => ch.stopAll());
      _channels = [];
      _client = null;

      set({ status: 'error', error: message });
    }
  },

  // ── Disconnect ──────────────────────────────────────────────
  disconnect: () => {
    _channels.forEach((ch) => ch.stopAll());
    _channels = [];
    _client = null;
    _seenOrderIds.clear();

    set({
      status: 'disconnected',
      error: null,
      keyHealth: [],
      balance: null,
      positions: [],
      openOrders: [],
      recentFills: [],
      activityTicker: [],
    });
  },

  // ── Remove Credentials (disconnect + clear storage) ─────────
  removeCredentials: () => {
    get().disconnect();
    localStorage.removeItem(STORAGE_KEY);
    set({ credentials: null });
  },

  // ── Refresh Positions ───────────────────────────────────────
  refreshPositions: async () => {
    if (!_client) return;
    try {
      const positions = await _client.getPositions();
      set({ positions });
    } catch (err) {
      console.warn('[AccountStore] refreshPositions failed:', err);
    }
  },

  // ── Refresh Orders ──────────────────────────────────────────
  refreshOrders: async () => {
    if (!_client) return;
    try {
      const openOrders = await _client.getOpenOrders();
      set({ openOrders });
    } catch (err) {
      console.warn('[AccountStore] refreshOrders failed:', err);
    }
  },

  // ── Process Fill (dedup layer) ──────────────────────────────
  processFill: (fill: KalshiFill) => {
    // Dedup check
    if (_seenFillIds.has(fill.fillId)) return;

    // Add to seen set (bounded)
    _seenFillIds.add(fill.fillId);
    _seenFillOrder.push(fill.fillId);
    while (_seenFillIds.size > MAX_SEEN_FILLS) {
      const oldest = _seenFillOrder.shift();
      if (oldest) _seenFillIds.delete(oldest);
    }

    // Prepend to recent fills
    const recentFills = [fill, ...get().recentFills].slice(0, MAX_RECENT_FILLS);
    const activityTicker = recentFills.slice(0, ACTIVITY_TICKER_SIZE);
    set({ recentFills, activityTicker });

    // Sound notification — age-gate suppresses historical fills on initial poll burst
    const fillAgeMs = Date.now() - new Date(fill.createdAt).getTime();
    const isRecent = fillAgeMs < 10_000;
    const snd = readSoundSettings();
    if (isRecent && snd.tradeSoundEnabled && fill.count >= snd.minFillContracts) {
      const isFirstFill = !_seenOrderIds.has(fill.orderId);
      _seenOrderIds.add(fill.orderId);

      const playFn = fill.count >= 1000 && snd.whaleSoundEnabled ? playWhaleTone
        : isFirstFill && snd.firstSaleSoundEnabled ? playKaChingTone
        : playFillTone;

      // If AudioContext is running, play immediately; otherwise queue for next user gesture
      const ctx = getAudioContext();
      if (ctx?.state === 'running') {
        playFn();
      } else {
        setPendingSound(playFn);
      }
    }

    // A fill means positions changed — refresh
    get().refreshPositions().catch(() => {});
  },

  // ── Place Order ─────────────────────────────────────────────
  placeOrder: async (input: OrderInput) => {
    if (!_client) throw new Error('Not connected');
    try {
      const order = await _client.placeOrder(input);
      const snd = readSoundSettings();
      if (snd.orderPlacedSoundEnabled) playOrderPlacedTone();
      get().refreshOrders().catch(() => {});
      return order;
    } catch (err) {
      const snd = readSoundSettings();
      if (snd.orderRejectionSoundEnabled) playOrderRejectedTone();
      throw err;
    }
  },

  // ── Cancel Order ────────────────────────────────────────────
  cancelOrder: async (orderId: string) => {
    if (!_client) throw new Error('Not connected');
    await _client.cancelOrder(orderId);
    // Refresh orders after canceling
    get().refreshOrders().catch(() => {});
  },
}));

// ── Helpers ─────────────────────────────────────────────────────

/** Channel-scoped fill fetch — uses only this channel's key for auth. */
async function channelFetchFills(
  ch: KeyChannel,
  env: 'prod' | 'demo',
  since?: string,
): Promise<KalshiFill[]> {
  ch.recordRequest('polling');
  const basePath = '/trade-api/v2/portfolio/fills';
  let fullPath = basePath;
  if (since) {
    const ts = Math.floor(new Date(since).getTime() / 1000);
    fullPath = `${basePath}?min_ts=${ts}`;
  }
  try {
    const headers = await buildHeaders(ch.accessKey, ch.cryptoKey, 'GET', basePath);
    const url = `${getBaseUrl(env)}${fullPath}`;
    const res = await fetch(url, { headers });
    if (!res.ok) {
      ch.reportError(res.status);
      throw new Error(`Fill poll ${res.status}`);
    }
    ch.reportSuccess();
    const data = (await res.json()) as Record<string, unknown>;
    return parseFillsResponse(data);
  } catch (err) {
    // Non-fatal for polling — let FillPoller retry next interval
    if (!(err instanceof Error && err.message.startsWith('Fill poll'))) {
      ch.reportError();
    }
    throw err;
  }
}

/** Parse fills from Kalshi's varying response envelope. */
function parseFillsResponse(data: Record<string, unknown>): KalshiFill[] {
  const raw = (data.fills ?? data.results ?? data.data ?? []) as Record<string, unknown>[];
  if (!Array.isArray(raw)) return [];

  return raw.map((f): KalshiFill => {
    const side = (f.side as 'yes' | 'no') ?? 'yes';

    // Dollar string → cents
    const dollarVal = f[`${side}_price_dollars`] ?? f.price_dollars;
    const centVal = f[`${side}_price`] ?? f.price;
    let price = 0;
    if (typeof dollarVal === 'string' && dollarVal !== '') {
      price = Math.round(parseFloat(dollarVal) * 100);
    } else if (typeof dollarVal === 'number') {
      price = dollarVal <= 1.01 ? Math.round(dollarVal * 100) : Math.round(dollarVal);
    } else if (typeof centVal === 'number') {
      price = Math.round(centVal);
    }

    const pickStr = (...c: unknown[]) => { for (const v of c) if (typeof v === 'string' && v !== '') return v; return undefined; };
    const pickNum = (...c: unknown[]) => { for (const v of c) { if (typeof v === 'number' && !isNaN(v)) return v; if (typeof v === 'string' && v !== '') { const n = parseFloat(v); if (!isNaN(n)) return n; } } return undefined; };

    return {
      fillId: pickStr(f.fill_id, f.trade_id, f.id) ?? `fill-${Date.now()}`,
      orderId: pickStr(f.order_id) ?? '',
      ticker: pickStr(f.ticker, f.market_ticker) ?? '',
      side,
      action: (f.action as 'buy' | 'sell') ?? 'buy',
      count: pickNum(f.count, f.count_fp, f.fill_count_fp, f.quantity) ?? 0,
      price,
      createdAt: pickStr(f.created_at, f.created_time) ?? new Date().toISOString(),
    };
  });
}
